'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNextStep } from 'nextstepjs';

import { useToast } from '@/components/ui/use-toast';
import { useCreateStore } from '@/lib/api/stores';
import { StoreForm, type StoreFormData } from '@/components/form/store';
import { StoreFormHeader } from '@/app/stores/create/components/store-form-header';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import currencies from '@/data/currencies.json';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { useOnboarding } from '@/hooks/use-onboarding';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Zap } from 'lucide-react';
import { useOrganization } from '@clerk/clerk-react';
import { STORE_ROUTES } from '@/config/router';
import { useEffectiveBranding } from '@/hooks/use-effective-branding';
import { useEffectiveLogoUrl } from '@/hooks/use-effective-logo-url';
import { isDemoSeedingEnabled } from '@/config/branding';
import { useSessionClaims } from '@/hooks/use-session-claims';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

const availableCurrencies = Object.keys(currencies);

const CreateStorePage = () => {
  const { t } = useTranslation(['stores', 'common']);
  const { orgRoutes, navigate, orgSlug } = useOrgNavigate();
  const { toast } = useToast();
  const { organization } = useOrganization();
  const { state: onboardingState, intentType, pluginName, complete, updateStep } = useOnboarding();
  const { closeNextStep } = useNextStep();

  const brand = useEffectiveBranding();
  const defaultLogoUrl = useEffectiveLogoUrl();
  const demoSeedingEnabled = isDemoSeedingEnabled();
  const { isAccountManager } = useSessionClaims();
  const createStoreMutation = useCreateStore();
  const [formError, setFormError] = React.useState<ReturnType<typeof parseTRPCError> | null>(null);

  // Update onboarding step when component mounts
  React.useEffect(() => {
    if (onboardingState && onboardingState.currentStep !== 'create_store') {
      updateStep('create_store');
    }
  }, [onboardingState, updateStep]);

  // Demo seeding is off by default; account managers opt in via the (AM-only) toggle.
  const defaultValues: StoreFormData = {
    name: '',
    presentmentCurrencies: ['USD'],
    chargeCurrency: 'USD',
    baseCurrency: 'USD',
    multipleSubscriptionsPerCustomerEnabled: true,
    createDemoProducts: false,
    // Branding defaults (from app branding config)
    primaryColor: brand.primaryColor,
    logoUrl: '',
    applyExternalBXGYDiscountOnSubscription: false,
    digitalProduct: false,
  };

  const handleCreateStore = async (data: StoreFormData) => {
    // IMPORTANT: Close the tour IMMEDIATELY before any async operations
    // This prevents "Cannot read properties of undefined (reading 'title')" error
    // The tour might auto-advance when clicking the submit button (which is the tour selector)
    closeNextStep();

    try {
      setFormError(null);

      const result = await createStoreMutation.mutateAsync({
        name: data.name,
        presentmentCurrencies: data.presentmentCurrencies,
        chargeCurrencies: [data.chargeCurrency],
        baseCurrency: data.baseCurrency,
        type: 'shopify',
        credentials: null,
        multipleSubscriptionsPerCustomerEnabled: data.multipleSubscriptionsPerCustomerEnabled,
        createDemoProducts: data.createDemoProducts,
        digitalProduct: data.digitalProduct,
        config: {
          primaryColor: data.primaryColor || brand.primaryColor,
          logoUrl: data.logoUrl || defaultLogoUrl,
        },
      });

      toast({
        title: t('storeCreated'),
        description: t('storeCreatedSuccess'),
      });

      // Always redirect to the newly created store's products page
      const storeRoutes = STORE_ROUTES(orgSlug, result.id);

      // If there's an onboarding intent, complete the onboarding flow
      if (onboardingState?.intent && result?.id) {
        updateStep('store_created');
        // Complete onboarding logic (clears state if needed)
        complete(orgSlug, result.id);
      }

      // Navigate to the store's products page
      navigate(storeRoutes.PRODUCTS());
    } catch (error) {
      const parsedError = parseTRPCError(error);
      setFormError(parsedError);

      if (!parsedError.isValidationError) {
        toast({
          title: t('createError'),
          description: parsedError.message,
          variant: 'destructive',
        });
      }
    }
  };

  const handleCancel = () => {
    navigate(orgRoutes.STORES());
  };

  const isSubmitting = createStoreMutation.isPending;

  // Get intent message
  const getIntentMessage = () => {
    if (!onboardingState?.intent) return null;

    switch (intentType) {
      case 'plugin_install':
        return {
          title: `Setting up ${pluginName}`,
          description: `Create a store to install ${pluginName || 'this plugin'}. You'll be redirected to the installation page after.`,
          icon: <Zap className="h-5 w-5 text-emerald-600" />,
        };
      case 'funnel_create':
        return {
          title: 'Setting up your funnel',
          description: "Create a store for your new funnel. You'll be able to configure it right after.",
          icon: <Zap className="h-5 w-5 text-emerald-600" />,
        };
      default:
        return null;
    }
  };

  const intentMessage = getIntentMessage();

  const header = (
    <StoreFormHeader
      title={t('createStore')}
      description={t('createStoreDescription')}
      onCancel={handleCancel}
      onSubmit={() => { }}
      submitLabel={
        isSubmitting
          ? t('creating')
          : t('createStore')
      }
      cancelLabel={t('cancel')}
      isSubmitting={isSubmitting}
    />
  );

  return (
    <div className="space-y-4">
      {/* Show context banner if there's an onboarding intent */}
      {intentMessage && (
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardHeader>
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-100">
                {intentMessage.icon}
              </div>
              <div className="flex-1">
                <CardTitle className="text-lg">{intentMessage.title}</CardTitle>
                <CardDescription className="mt-1 text-sm">{intentMessage.description}</CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      )}

      <StoreForm
        baseCurrencyEditable={true}
        defaultValues={defaultValues}
        currencies={availableCurrencies}
        onSubmit={handleCreateStore}
        formError={formError}
        onDismissError={() => setFormError(null)}
        header={header}
        basicInformationTitle={t('basicInformation')}
        currenciesTitle={t('currencies')}
        settingsTitle={t('settings')}
        showDemoSeeding={isAccountManager && demoSeedingEnabled}
      />
    </div>
  );
};

export default withRequiredPermission(CreateStorePage, PERMISSIONS.STORES_MANAGE);
